<template>
  <div id="searchKeyRankMgmt" class="search-key-rank-mgmt">
    <div class="breadcrumb-box">
      <el-breadcrumb separator="/">
        <el-breadcrumb-item>销售分析</el-breadcrumb-item>
        <el-breadcrumb-item>搜索词排名</el-breadcrumb-item>
      </el-breadcrumb>
    </div>

    <div class="section-card">
      <div class="section-title">查询条件</div>
      <div class="header-line">
        <span class="header-label">ASIN</span>
        <el-input
          v-model="query.asin"
          size="small"
          clearable
          placeholder="必填"
          style="width: 160px"
          @keyup.enter.native="onQuery"
        />
        <span class="header-label">店铺</span>
        <el-select v-model="query.shopId" size="small" clearable placeholder="全部" style="width: 120px">
          <el-option v-for="item in shopOptions" :key="item.id" :label="item.name" :value="item.id" />
        </el-select>
        <el-button type="primary" size="small" @click="onQuery">查询</el-button>
      </div>
      <div v-if="goodsTitle" class="goods-title-hint">商品：{{ goodsTitle }}</div>
    </div>

    <div class="main-box">
      <el-table
        v-loading="listLoading"
        :data="tableData"
        border
        stripe
        size="small"
        :height="tableHeight"
        style="width: 100%"
      >
        <el-table-column type="index" label="#" width="50" align="center" />
        <el-table-column prop="searchKey" label="搜索词" min-width="200" show-overflow-tooltip />
        <el-table-column prop="page" label="页数" width="70" align="center" />
        <el-table-column prop="index" label="页内排名" width="90" align="center" />
        <el-table-column prop="version" label="最新版本" width="100" align="center" />
        <el-table-column prop="shopName" label="店铺" width="90" show-overflow-tooltip />
        <el-table-column label="更新时间" width="170">
          <template slot-scope="scope">{{ formatTime(scope.row.updateTime) }}</template>
        </el-table-column>
        <el-table-column label="操作" width="100" align="center" fixed="right">
          <template slot-scope="scope">
            <el-button type="text" size="small" @click="openHistory(scope.row)">历史表现</el-button>
          </template>
        </el-table-column>
      </el-table>

      <div class="pager-wrap">
        <el-pagination
          background
          layout="total, sizes, prev, pager, next, jumper"
          :page-sizes="[20, 50, 100, 200]"
          :current-page.sync="currentPage"
          :page-size.sync="pageSize"
          :total="totalRecords"
          @size-change="onPageSizeChange"
          @current-change="loadList"
        />
      </div>
    </div>

    <el-dialog
      :title="historyDialogTitle"
      :visible.sync="historyVisible"
      width="860px"
      append-to-body
      @opened="onHistoryDialogOpened"
      @closed="onHistoryDialogClosed"
    >
      <div class="history-toolbar">
        <el-radio-group v-model="historyViewMode" size="small" @change="onHistoryViewChange">
          <el-radio-button label="chart">图表</el-radio-button>
          <el-radio-button label="table">表格</el-radio-button>
        </el-radio-group>
      </div>

      <div v-show="historyViewMode === 'chart'" v-loading="historyLoading" class="history-chart-wrap">
        <div ref="historyChart" class="history-chart" />
        <div v-if="!historyLoading && historyData.length === 0" class="history-chart-empty">暂无历史数据</div>
      </div>

      <el-table
        v-show="historyViewMode === 'table'"
        v-loading="historyLoading"
        :data="historyData"
        border
        stripe
        size="small"
        max-height="420"
      >
        <el-table-column prop="version" label="版本" width="100" align="center">
          <template slot-scope="scope">{{ formatVersion(scope.row.version) }}</template>
        </el-table-column>
        <el-table-column prop="page" label="页数" width="70" align="center" />
        <el-table-column prop="index" label="页内排名" width="90" align="center" />
        <el-table-column label="综合排名" width="90" align="center">
          <template slot-scope="scope">{{ calcCompositeRank(scope.row) }}</template>
        </el-table-column>
        <el-table-column label="更新时间" min-width="170">
          <template slot-scope="scope">{{ formatTime(scope.row.updateTime) }}</template>
        </el-table-column>
      </el-table>
    </el-dialog>
  </div>
</template>

<script>
import * as echarts from "echarts";
import { goodsKeyRankModules } from "@r";

const PAGE_SIZE = 48;

const SHOP_OPTIONS = [
  { id: 1, name: "灵柚" },
  { id: 2, name: "利尔" },
  { id: 3, name: "臻昂" },
  { id: 4, name: "萧发" },
  { id: 5, name: "誉腾" }
];

export default {
  name: "SearchKeyRankMgmt",
  data() {
    return {
      shopOptions: SHOP_OPTIONS,
      query: {
        asin: "",
        shopId: null
      },
      goodsTitle: "",
      tableData: [],
      listLoading: false,
      currentPage: 1,
      pageSize: 50,
      totalRecords: 0,
      tableHeight: document.body.clientHeight - 64 - 220,
      historyVisible: false,
      historyLoading: false,
      historyData: [],
      historyRow: null,
      historyViewMode: "chart",
      historyChartInstance: null
    };
  },
  computed: {
    historyDialogTitle() {
      if (!this.historyRow) return "历史表现";
      return `历史表现 - ${this.historyRow.searchKey || ""}`;
    }
  },
  activated() {
    const asin = this.$route.query.asin;
    if (asin) {
      this.query.asin = asin;
      this.onQuery();
    }
  },
  mounted() {
    this.$eventBus.$on("resize", this.onWindowResize);
  },
  beforeDestroy() {
    this.$eventBus.$off("resize", this.onWindowResize);
    this.disposeHistoryChart();
  },
  methods: {
    onWindowResize() {
      this.calculateHeight();
      this.resizeHistoryChart();
    },
    calculateHeight() {
      this.tableHeight = document.body.clientHeight - 64 - 220;
    },
    formatVersion(version) {
      if (version == null || version === "") return "—";
      const str = String(version);
      if (str.length === 8) {
        return str.substring(0, 4) + "-" + str.substring(4, 6) + "-" + str.substring(6, 8);
      }
      return str;
    },
    calcCompositeRank(row) {
      if (row.page == null || row.index == null) return "—";
      return (Number(row.page) - 1) * PAGE_SIZE + Number(row.index);
    },
    formatTime(ts) {
      if (ts == null || ts === "") return "—";
      const d = new Date(Number(ts));
      if (isNaN(d.getTime())) return "—";
      const pad = n => (n < 10 ? "0" + n : "" + n);
      return (
        d.getFullYear() +
        "-" +
        pad(d.getMonth() + 1) +
        "-" +
        pad(d.getDate()) +
        " " +
        pad(d.getHours()) +
        ":" +
        pad(d.getMinutes()) +
        ":" +
        pad(d.getSeconds())
      );
    },
    onQuery() {
      const asin = (this.query.asin || "").trim();
      if (!asin) {
        this.$message.warning("请输入 ASIN");
        return;
      }
      this.currentPage = 1;
      this.loadList();
    },
    onPageSizeChange() {
      this.currentPage = 1;
      this.loadList();
    },
    loadList() {
      const asin = (this.query.asin || "").trim();
      if (!asin) return;
      const params = {
        asin,
        currentPage: this.currentPage,
        pageSize: Math.min(this.pageSize, 200)
      };
      if (this.query.shopId != null && this.query.shopId !== "") {
        params.shopId = this.query.shopId;
      }
      this.listLoading = true;
      goodsKeyRankModules
        .page(params)
        .then(res => {
          this.listLoading = false;
          if (res.code !== "00000") {
            this.tableData = [];
            this.totalRecords = 0;
            this.goodsTitle = "";
            this.$message.error(res.msg || "查询失败");
            return;
          }
          const page = res.data || {};
          this.tableData = page.records || [];
          this.totalRecords = page.total != null ? Number(page.total) : 0;
          if (page.current != null) this.currentPage = Number(page.current);
          if (page.size != null) this.pageSize = Number(page.size);
          this.goodsTitle =
            this.tableData.length > 0 && this.tableData[0].title ? this.tableData[0].title : "";
        })
        .catch(err => {
          this.listLoading = false;
          this.tableData = [];
          this.totalRecords = 0;
          this.goodsTitle = "";
          this.$message.error((err && err.msg) || "查询失败");
        });
    },
    openHistory(row) {
      const asin = (this.query.asin || "").trim();
      if (!asin || !row.searchKey) return;
      this.historyRow = row;
      this.historyViewMode = "chart";
      this.historyVisible = true;
      this.historyLoading = true;
      this.historyData = [];
      this.disposeHistoryChart();
      const params = { asin, searchKey: row.searchKey };
      if (row.shopId != null) {
        params.shopId = row.shopId;
      } else if (this.query.shopId != null && this.query.shopId !== "") {
        params.shopId = this.query.shopId;
      }
      goodsKeyRankModules
        .history(params)
        .then(res => {
          this.historyLoading = false;
          if (res.code !== "00000") {
            this.$message.error(res.msg || "加载历史失败");
            return;
          }
          this.historyData = res.data || [];
          this.$nextTick(() => {
            if (this.historyViewMode === "chart") {
              this.initHistoryChart();
            }
          });
        })
        .catch(err => {
          this.historyLoading = false;
          this.$message.error((err && err.msg) || "加载历史失败");
        });
    },
    onHistoryDialogOpened() {
      if (this.historyViewMode === "chart" && this.historyData.length > 0) {
        this.$nextTick(() => this.initHistoryChart());
      }
    },
    onHistoryDialogClosed() {
      this.disposeHistoryChart();
    },
    onHistoryViewChange(mode) {
      if (mode === "chart") {
        this.$nextTick(() => this.initHistoryChart());
      } else {
        this.disposeHistoryChart();
      }
    },
    getSortedHistory() {
      return [...this.historyData].sort((a, b) => Number(a.version) - Number(b.version));
    },
    initHistoryChart() {
      const chartDom = this.$refs.historyChart;
      if (!chartDom || this.historyData.length === 0) return;

      this.disposeHistoryChart();
      const sorted = this.getSortedHistory();
      const dates = sorted.map(h => this.formatVersion(h.version));
      const compositeData = sorted.map(h => {
        if (h.page == null || h.index == null) return null;
        return (Number(h.page) - 1) * PAGE_SIZE + Number(h.index);
      });
      const pageData = sorted.map(h => (h.page != null ? Number(h.page) : null));
      const indexData = sorted.map(h => (h.index != null ? Number(h.index) : null));

      const chart = echarts.init(chartDom);
      this.historyChartInstance = chart;

      chart.setOption({
        tooltip: {
          trigger: "axis",
          formatter: params => {
            let html = (params[0] && params[0].axisValue) || "";
            html += "<br/>";
            params.forEach(p => {
              if (p.value != null && p.value !== "") {
                html += p.marker + " " + p.seriesName + ": " + p.value + "<br/>";
              }
            });
            const idx = params[0] ? params[0].dataIndex : -1;
            if (idx >= 0 && sorted[idx]) {
              const row = sorted[idx];
              html += "页数: " + (row.page != null ? row.page : "—");
              html += "，页内排名: " + (row.index != null ? row.index : "—");
            }
            return html;
          }
        },
        legend: {
          data: ["综合排名", "页数", "页内排名"],
          bottom: 0
        },
        grid: {
          left: 55,
          right: 55,
          top: 35,
          bottom: 48
        },
        xAxis: {
          type: "category",
          data: dates,
          boundaryGap: false,
          axisLabel: { rotate: dates.length > 8 ? 35 : 0 }
        },
        yAxis: [
          {
            type: "value",
            name: "综合排名",
            inverse: true,
            min: "dataMin",
            axisLabel: { formatter: "{value}" }
          },
          {
            type: "value",
            name: "页数/页内排名",
            inverse: true,
            min: 1,
            axisLabel: { formatter: "{value}" }
          }
        ],
        series: [
          {
            name: "综合排名",
            type: "line",
            yAxisIndex: 0,
            data: compositeData,
            connectNulls: false,
            symbolSize: 7,
            itemStyle: { color: "#5470c6" },
            lineStyle: { width: 2 }
          },
          {
            name: "页数",
            type: "line",
            yAxisIndex: 1,
            data: pageData,
            connectNulls: true,
            symbolSize: 6,
            itemStyle: { color: "#91cc75" }
          },
          {
            name: "页内排名",
            type: "line",
            yAxisIndex: 1,
            data: indexData,
            connectNulls: true,
            symbolSize: 6,
            itemStyle: { color: "#ee6666" }
          }
        ]
      });
    },
    disposeHistoryChart() {
      if (this.historyChartInstance) {
        this.historyChartInstance.dispose();
        this.historyChartInstance = null;
      }
    },
    resizeHistoryChart() {
      if (this.historyChartInstance) {
        this.historyChartInstance.resize();
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.search-key-rank-mgmt {
  width: 100%;
  margin: 0 auto;
  background: white;
  padding: 12px 16px 16px;
}
.breadcrumb-box {
  margin-bottom: 12px;
}
.section-card {
  margin-bottom: 12px;
  padding: 12px 14px;
  border: 1px solid #ebeef5;
  border-radius: 4px;
}
.section-title {
  font-weight: 600;
  margin-bottom: 10px;
  color: #303133;
}
.header-line {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}
.header-label {
  color: #606266;
  font-size: 13px;
}
.goods-title-hint {
  margin-top: 8px;
  color: #606266;
  font-size: 13px;
  line-height: 1.5;
}
.main-box {
  margin-top: 4px;
}
.pager-wrap {
  margin-top: 12px;
  text-align: right;
}
.history-toolbar {
  margin-bottom: 12px;
}
.history-chart-wrap {
  position: relative;
  min-height: 380px;
}
.history-chart {
  width: 100%;
  height: 380px;
}
.history-chart-empty {
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  line-height: 380px;
  text-align: center;
  color: #909399;
  font-size: 14px;
}
</style>
